/**
 * Adapted from PlayCanvas Engine's gsplat-octree.resource.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { Disposable } from '@/shared/types/core'

export interface GSplatOctreeOwner extends Disposable {
  readonly nodes?: readonly {
    readonly lods: readonly ({ readonly count: number } | undefined)[]
  }[]
  readonly numSplats?: number
}

export interface GSplatOctreeResourceOptions<TMetadata> {
  readonly aabb: THREE.Box3
  readonly metadata: TMetadata
  readonly octree: GSplatOctreeOwner
  readonly generation?: number
}

/** Octree dataset Resource 只拥有 metadata/octree；具体 file Resource 保持各自格式。 */
export class GSplatOctreeResource<TMetadata = unknown> implements Disposable {
  readonly kind = 'octree' as const
  readonly aabb: THREE.Box3
  /** 与上游 `data` 同义：保留扩展字段，但不再持有已消费的 tree hierarchy。 */
  readonly data: TMetadata
  readonly metadata: TMetadata
  readonly centersVersion = 0
  readonly generation: number
  readonly object3D = null
  private octreeValue: GSplatOctreeOwner | null
  private numSplatsValue: number | undefined
  private refCount = 0
  private pendingDestroy = false
  private destroyed = false

  constructor(options: GSplatOctreeResourceOptions<TMetadata>) {
    this.aabb = options.aabb.clone()
    this.data = releaseRetainedTree(options.metadata)
    this.metadata = this.data
    this.octreeValue = options.octree
    this.generation = options.generation ?? 1
  }

  get octree(): GSplatOctreeOwner | null {
    return this.octreeValue
  }

  get boundingBox(): THREE.Box3 {
    return this.aabb
  }

  get numSplats(): number {
    if (this.numSplatsValue === undefined) {
      const nodes = this.octreeValue?.nodes
      if (nodes) {
        let total = 0
        for (const node of nodes) total += node.lods[0]?.count ?? 0
        this.numSplatsValue = total
      } else {
        this.numSplatsValue = this.octreeValue?.numSplats ?? 0
      }
    }
    return this.numSplatsValue
  }

  getCpuExportData(): TMetadata | null {
    return this.destroyed ? null : this.data
  }

  acquire(): GSplatResourceLease {
    if (this.destroyed) throw new Error('GSplat Octree Resource 已销毁')
    this.refCount++
    const owner = this
    let released = false
    return {
      get resourceIdentity() {
        return owner
      },
      get numSplats() {
        return owner.numSplats
      },
      get boundingBox() {
        return owner.boundingBox
      },
      get aabb() {
        return owner.aabb
      },
      get object3D() {
        return owner.object3D
      },
      get generation() {
        return owner.generation
      },
      resourceKind: 'octree' as const,
      streaming: 'lod' as const,
      octreeSemantics: 'dataset' as const,
      get centersVersion() {
        return owner.centersVersion
      },
      getCpuExportData: () => owner.getCpuExportData(),
      acquire: () => owner.acquire(),
      dispose: () => {
        if (released) return
        released = true
        owner.releaseReference()
      },
    }
  }

  destroy(): void {
    if (this.destroyed || this.pendingDestroy) return
    if (this.refCount > 0) {
      this.pendingDestroy = true
      return
    }
    this.actualDestroy()
  }

  private releaseReference(): void {
    if (this.destroyed || this.refCount === 0) return
    this.refCount--
    if (this.refCount === 0 && this.pendingDestroy) this.actualDestroy()
  }

  private actualDestroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.pendingDestroy = false
    this.octreeValue?.dispose()
    this.octreeValue = null
  }

  dispose(): void {
    this.destroy()
  }
}

function releaseRetainedTree<TMetadata>(metadata: TMetadata): TMetadata {
  if (!metadata || typeof metadata !== 'object' || !('tree' in metadata)) return metadata
  return { ...metadata, tree: null } as TMetadata
}
